import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories, categoryLabels } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'korean');

  const currentCategory = categories.find((c) => c.slug === activeTab);
  const filteredComics = comics.filter((c) => c.category === activeTab);

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    window.history.pushState(null, '', `/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const catMeta: Record<string, { title: string; desc: string; keywords: string }> = {
    korean: {
      title: '韩漫 - 99漫画免费韩国漫画推荐 全集大全',
      desc: '99漫画韩漫分类，为你推荐最热门的韩国漫画，画风精美剧情丰富，支持下拉式阅读追更收藏。韩漫全集大全尽在99漫画。',
      keywords: '韩漫,免费韩漫,韩国漫画,韩漫推荐,99漫画',
    },
    japanese: {
      title: '日漫 - 99漫画免费日本漫画推荐 全集大全',
      desc: '99漫画日漫分类，为你推荐经典日本漫画，热血治愈应有尽有，支持下拉式阅读追更收藏。日漫全集大全尽在99漫画。',
      keywords: '日漫,免费日漫,日本漫画,日漫推荐,99漫画',
    },
    chinese: {
      title: '国漫 - 99漫画免费国产漫画推荐 全集大全',
      desc: '99漫画国漫分类，为你推荐优秀国产漫画，品质保证精彩不断，支持下拉式阅读追更收藏。国漫全集大全尽在99漫画。',
      keywords: '国漫,免费国漫,国产漫画,国漫推荐,99漫画',
    },
  };

  const meta = catMeta[activeTab] || catMeta.korean;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://99manga.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory?.name || '韩漫', item: `https://99manga.com/category/${activeTab}` },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: meta.title,
    url: `https://99manga.com/category/${activeTab}`,
    description: meta.desc,
    isPartOf: { '@type': 'WebSite', name: '99漫画', url: 'https://99manga.com' },
    about: currentCategory?.name,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <section className="bg-background-100 py-10 md:py-14">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">{currentCategory?.name}</span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">{meta.title}</h1>
                <p className="text-sm text-foreground-500 max-w-2xl">{meta.desc}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 p-1 bg-background-50 rounded-full border border-background-200/70 w-fit">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 99漫画${categoryLabels[comic.category]}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${
                          comic.status === 'ongoing' ? 'bg-primary-500 text-background-50' : 'bg-foreground-600 text-background-50'
                        }`}>
                          {comic.status === 'ongoing' ? '连载中' : '已完结'}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded px-1.5 py-0.5">
                        <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                        <span className="text-background-50 text-[10px] font-medium">{comic.rating}</span>
                      </div>
                    </div>
                    <h3 className="font-medium text-sm text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                      {comic.author} · {comic.chapters}话
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-1.5 py-0.5 bg-secondary-100 text-secondary-700 rounded text-[10px] whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                <Link to="/" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}